import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ANIMATIONS, planFor, voiceName, type BriefDraft, type ProjectView } from "@joogoos/contracts";
import { api, ApiError } from "../api.js";
import { Icon } from "../ui/icons.js";
import { ChatModelPicker } from "../ui/ChatModel.js";
import { Gallery } from "./stages/Gallery.js";

const FORMATS = [["story", "Story 9:16"], ["square", "Square"], ["portrait", "Portrait 4:5"], ["landscape", "Landscape"]] as const;

/**
 * The answer that wants no voice. It said "Captions only" until 2026-09-28, but a video with no voice has no captions in
 * any style: the words on screen carry it. Worded for every style, since it can be asked before the style is chosen.
 */
const NO_NARRATION = "No narration, just the words on screen";

/**
 * The conversation (docs/03), and zone 2 of the Studio.
 *
 * What is typed goes to the Studio guide (ADR 0031), which works out what it decides and suggests the rest. The buttons
 * answer one thing each and cost nothing. A choice that has to be looked at — which photos, which voice — is not asked
 * here; it opens zone 3, and the chat says what is being chosen while it does. That division is the rule: words in the
 * chat, a selection in the pane.
 *
 * When there is nothing yet it is a greeting and one composer. On a desk the gallery sits beside it in zone 3; on a
 * phone, where there is one column, it sits underneath.
 */
export function Chat({ project, onStart, starting, startError, wide, gallery = true, onPhotos }: {
  project: ProjectView | null;
  /** The first answer, before a project exists: a button's, as a patch, or words for the guide, with no patch. */
  onStart: (patch: BriefDraft | null, text: string) => void;
  starting: boolean;
  /** Opens the photo picker, from the composer's Photos button. */
  onPhotos?: (() => void) | undefined;
  startError?: Error | null | undefined;
  /** True on a phone while the chat has the screen. It reads as one centred column, not a pane. */
  wide?: boolean | undefined;
  /** Whether the opening screen carries the gallery under the composer. Not on a desk, where zone 3 holds it. */
  gallery?: boolean | undefined;
}) {
  // The opening is for before anything has been said. Once the owner has written or tapped something, the guide's
  // answer is on screen even when it is a question back rather than an idea taken (found on the dev box, 2026-09-25).
  const said = !!project && (!!project.draft.idea || project.messages.some((m) => m.role === "user"));
  return said
    ? <Thread project={project} wide={wide} onPhotos={onPhotos} />
    : <Opening project={project} onStart={onStart} starting={starting} startError={startError} gallery={gallery} onPhotos={onPhotos} />;
}

/**
 * Before there is an idea: the only question is what we are making, so the chat asks it once, large, with the
 * gallery as the other way in (03 — forwards through the chat, backwards from something that works).
 */
function Opening({ project, onStart, starting, startError, gallery, onPhotos }: {
  onPhotos?: (() => void) | undefined;
  project: ProjectView | null;
  onStart: (patch: BriefDraft | null, text: string) => void;
  starting: boolean;
  startError?: Error | null | undefined;
  gallery: boolean;
}) {
  const qc = useQueryClient();
  const turn = useMutation({
    mutationFn: (text: string) => api.turn(project!.id, text),
    onSuccess: (p) => { qc.setQueryData(["project", p.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
  const send = (text: string) => (project ? turn.mutate(text) : onStart(null, text));
  const error = turn.error ?? startError;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="mx-auto max-w-[680px] px-4 md:px-6 pt-8 md:pt-10 flex flex-col gap-4">
        <h1 className="font-display text-[30px] leading-[1.1] font-bold tracking-[-.03em] text-balance">
          What are we making today?
        </h1>
        <p className="text-[13.5px] text-muted leading-relaxed -mt-2">
          Tell me in a sentence. Before anything is made, I will show you the plan and about what it costs.
        </p>
        <Composer placeholder="e.g. fresh sourdough every Saturday morning" onSend={send} disabled={starting || turn.isPending} autoFocus onPhotos={onPhotos} />
        {starting || turn.isPending ? <p className="text-xs text-muted">Starting…</p> : null}
        {error ? <Failure error={error} /> : null}
      </div>
      {gallery ? (
        <div className="mx-auto max-w-[1000px] px-2 pb-8">
          <Gallery project={project} onStart={onStart} starting={starting} />
        </div>
      ) : null}
    </div>
  );
}

function Thread({ project, wide, onPhotos }: { project: ProjectView; wide?: boolean | undefined; onPhotos?: (() => void) | undefined }) {
  const qc = useQueryClient();
  const set = (p: ProjectView) => { qc.setQueryData(["project", project.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); };
  const answer = useMutation({
    mutationFn: ({ patch, text, clear }: { patch: BriefDraft; text?: string; clear?: string[] }) => api.answer(project.id, patch, text, clear),
    onSuccess: set,
  });
  const turn = useMutation({ mutationFn: (text: string) => api.turn(project.id, text), onSuccess: set });
  const generate = useMutation({ mutationFn: () => api.generate(project.id), onSuccess: set });
  const chatting = project.status === "chatting";
  const q = project.nextQuestion;
  const ready = chatting && project.missing.length === 0;
  const say = (text: string) => { turn.reset(); turn.mutate(text); };
  // What was just typed stays on screen while the guide answers, rather than vanishing until the reply arrives.
  const waiting = turn.isPending ? turn.variables : undefined;

  // Centred and held to a readable measure when it has the room, hard against the pane when it does not.
  const column = wide ? "mx-auto w-full max-w-[680px]" : "";
  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className={`flex-1 overflow-y-auto p-3.5 flex flex-col gap-2.5 ${wide ? "px-6 pt-8" : ""}`}>
        <div className={`${column} flex flex-col gap-2.5`}>
        {project.messages.filter((m) => m.content.text?.trim()).map((m) => (
          <Bubble key={m.id} mine={m.role === "user"}>{m.content.text}</Bubble>
        ))}
        {waiting ? (
          <>
            <Bubble mine>{waiting}</Bubble>
            <Bubble><span className="text-muted" aria-label="The Studio guide is answering">…</span></Bubble>
          </>
        ) : null}
        {turn.error ? <Failure error={turn.error} /> : null}
        {answer.error ? <Failure error={answer.error} /> : null}
        {generate.error ? <Failure error={generate.error} /> : null}
        </div>
      </div>

      <div className={`p-3.5 flex flex-col gap-2.5 ${wide ? "px-6 pb-6" : "border-t border-line"} ${column}`}>
        {chatting ? (
          <Suggestions
            project={project}
            busy={answer.isPending || turn.isPending}
            onTakeBack={(key, label) => answer.mutate({ patch: {}, text: `Not ${label.charAt(0).toLowerCase()}${label.slice(1)}`, clear: [key] })}
          />
        ) : null}
        {ready ? (
          // Everything is answered, so the chat carries the action rather than sending you to the other pane for
          // it. The same button, with the same price, closes the plan in zone 3.
          <GenerateButton plan={planFor(project.draft).totalUsd} busy={generate.isPending} onClick={() => generate.mutate()} />
        ) : !chatting ? (
          // Nothing reads what is typed once it is made, so there is no box to type it in (docs/14).
          <p className="text-[13px] text-muted leading-relaxed">
            The chat does not take changes yet. To change a shot, select it and press Rerun.
          </p>
        ) : q?.field === "kind" ? (
          <Chips options={[["video", "A short video"], ["post", "A post"]]} onPick={(v, label) => answer.mutate({ patch: { kind: v as "post" | "video" }, text: label })} />
        ) : q?.field === "format" ? (
          <Chips options={FORMATS} onPick={(v, label) => answer.mutate({ patch: { format: v as BriefDraft["format"] }, text: label })} />
        ) : q?.field === "sourceMediaIds" ? (
          <Aside>Pick them on the right — yours, from your website, or from your posts.</Aside>
        ) : q?.field === "voice" ? (
          // The one text answer to a question whose real answer is a selection: wanting no voice at all needs no
          // listening to, so it stays here rather than becoming a tile in the catalogue.
          <>
            <Aside>Pick a voice on the right, or:</Aside>
            <Chips options={[["off", NO_NARRATION]]} onPick={(_, label) => answer.mutate({ patch: { voice: { enabled: false } }, text: label })} />
          </>
        ) : q?.field === "animation" ? (
          // The catalogue is short enough to be words (03): a name and what it does, and "none" first because it is
          // the answer most videos want. Previews belong in the pane the day the catalogue outgrows a row of chips.
          <Chips
            options={[["none", "No animation"], ...ANIMATIONS.map((a) => [a.id, `${a.name} · ${a.note}`] as const)]}
            onPick={(v, label) => answer.mutate({ patch: { animation: v === "none" ? { enabled: false } : { enabled: true, animationId: v } }, text: label })}
          />
        ) : null}

        {chatting ? (
          <Composer placeholder={placeholderFor(ready ? "ready" : q?.field)} onSend={say} disabled={turn.isPending} onPhotos={onPhotos} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * What the guide suggested and nobody has decided yet (ADR 0031), each a tap to take back.
 *
 * Suggestions are shown apart from what the owner chose because a guess that reads as a decision is how a video gets
 * made in the wrong shape. Taking one back costs nothing: the question it answered comes back with its buttons.
 */
function Suggestions({ project, busy, onTakeBack }: { project: ProjectView; busy: boolean; onTakeBack: (key: string, label: string) => void }) {
  const items = project.proposed.flatMap((key) => {
    const label = suggestionLabel(key, project.draft);
    return label ? [[key, label] as const] : [];
  });
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[12px] text-muted mr-0.5">Suggested:</span>
      {items.map(([key, label]) => (
        <button
          key={key}
          onClick={() => onTakeBack(key, label)}
          disabled={busy}
          title="Not this one"
          aria-label={`Not ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
          className="flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[12px] border border-dashed border-line-2 bg-panel hover:bg-page disabled:opacity-40"
        >
          {label}
          <Icon name="x" size={11} className="text-faint" />
        </button>
      ))}
    </div>
  );
}

/** A suggestion, the way the chips that answer the same question say it. */
function suggestionLabel(key: string, d: BriefDraft): string | null {
  switch (key) {
    case "kind": return d.kind === "post" ? "A post" : d.kind === "video" ? "A short video" : null;
    case "format": return FORMATS.find(([f]) => f === d.format)?.[1] ?? null;
    case "voice": return d.voice ? (d.voice.enabled ? `Narrated by ${voiceName(d.voice.voiceId)}` : NO_NARRATION) : null;
    case "onScreenText": return d.onScreenText ? `“${d.onScreenText}” on screen` : null;
    case "durationMs": return d.durationMs ? `${Math.round(d.durationMs / 1000)} seconds` : null;
    // Suggested only while the questions are skipped (STUDIO_QUESTIONS, docs/03); taking one back opens the pane or the chips.
    // A drawn video has no pictures to make, so there is nothing to take back.
    case "sourceMediaIds": return d.look === "drawn" && d.kind === "video" ? null : d.sourceMediaIds ? (d.sourceMediaIds.length ? `${d.sourceMediaIds.length} of your photos` : "Generated pictures") : null;
    case "animation": return d.animation ? (d.animation.enabled ? ANIMATIONS.find((a) => a.id === d.animation?.animationId)?.name ?? "An animation" : "Plain, no animation") : null;
    default: return null;
  }
}

/**
 * The price is on the button, because that is the moment it is decided (03, ADR 0012).
 *
 * `stopped` is a ceiling or an empty balance in the way. It is deliberately **not disabled**: a dead button teaches
 * people that the product is broken, and pressing this one brings back the dialog that says which ceiling it was
 * and raises it in a tap (ADR 0012 point 3).
 */
export function GenerateButton({ plan, busy, onClick, subtle, stopped }: {
  plan: string; busy: boolean; onClick: () => void; subtle?: boolean; stopped?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={stopped ? "A limit is in the way — press to see which" : undefined}
      className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 ${
        stopped ? "border border-line-2 bg-panel text-ink-2 hover:bg-page" : subtle ? "border border-line-2 bg-panel hover:bg-page" : "bg-coral text-white hover:bg-coral-2"
      }`}
    >
      {busy
        ? "Starting…"
        : stopped
          ? <>Generate <span className="tabular-nums font-medium text-muted">at your limit</span></>
          : <>Generate <span className={`tabular-nums font-medium ${subtle ? "text-muted" : "text-white/75"}`}>about ${plan}</span></>}
    </button>
  );
}

/** A failure, with the reference the API made for it — the number a user reads down the phone (docs/16). */
function Failure({ error }: { error: Error }) {
  return (
    <div className="text-bad text-xs">
      {error.message}
      {error instanceof ApiError && error.ref ? <span className="opacity-70 font-mono"> · ref {error.ref}</span> : null}
    </div>
  );
}

/** One message: the owner's on the right in ink, the guide's on the left. */
function Bubble({ mine, children }: { mine?: boolean; children: React.ReactNode }) {
  return (
    <div className={`max-w-[85%] rounded-xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-line ${mine ? "ml-auto bg-ink text-white" : "bg-page text-ink"}`}>
      {children}
    </div>
  );
}

/** What the chat says while zone 3 holds the question. */
function Aside({ children }: { children: React.ReactNode }) {
  return <p className="text-[12.5px] text-muted leading-relaxed">{children}</p>;
}

/**
 * One rounded box: what you type, your photos, which assistant answers, and send. "Attach" and "Dictate" did nothing
 * and went (2026-09-27); Photos opens the photo picker beside the chat.
 */
function Composer({ placeholder, onSend, disabled, autoFocus, onPhotos }: { placeholder: string; onSend: (t: string) => void; disabled?: boolean; autoFocus?: boolean; onPhotos?: (() => void) | undefined }) {
  const [t, setT] = useState("");
  function send() { const v = t.trim(); if (v && !disabled) { onSend(v); setT(""); } }
  return (
    <div className="border border-line-2 rounded-2xl bg-panel px-3.5 pt-3 pb-2.5 flex flex-col gap-3 shadow-[0_1px_3px_rgba(23,23,26,.06)] focus-within:border-ink/30">
      <textarea
        value={t}
        onChange={(e) => setT(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        rows={1}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="resize-none bg-transparent text-sm outline-none placeholder:text-faint field-sizing-content max-h-32"
      />
      <div className="flex items-center gap-1.5">
        {onPhotos ? (
          <button onClick={onPhotos} disabled={disabled} title="Choose your photos" aria-label="Choose your photos" className="h-7 px-2 rounded-lg flex items-center gap-1.5 text-[12px] text-muted hover:bg-page hover:text-ink disabled:opacity-40">
            <Icon name="image" size={16} /> Photos
          </button>
        ) : null}
        <span className="ml-auto"><ChatModelPicker compact /></span>
        <button onClick={send} disabled={!t.trim() || disabled} title="Send" className="w-8 h-8 rounded-full bg-ink text-white grid place-items-center disabled:opacity-30">
          <Icon name="send" size={15} />
        </button>
      </div>
    </div>
  );
}

function placeholderFor(field: string | undefined) {
  switch (field) {
    case "idea": return "e.g. fresh sourdough every Saturday morning";
    case "ready": return "Anything to change before I start?";
    default: return "…or say it in your own words";
  }
}

function Chips({ options, onPick }: { options: ReadonlyArray<readonly [string, string]>; onPick: (v: string, label: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([v, label], i) => (
        <button
          key={v}
          onClick={() => onPick(v, label)}
          className={`px-3.5 py-1.5 rounded-full text-[13px] border ${
            i === 0 ? "bg-ink text-white border-ink font-semibold" : "bg-panel border-line-2 hover:bg-page"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
